import React, { useEffect, useMemo } from 'react'
import ImGoodAtService from './Service'
import { IImGoodAtProvider } from './Interface'

import UserResponsesManagerService from 'smart-components/user-responses-manager/Service'
import { useServiceState, useNotification } from 'helpers'

import { IResponsesValue } from 'types'

const Provider: React.FC<IImGoodAtProvider> = ({ children, disabled, responses }) => {
  const iAmGoodAtState = useServiceState(ImGoodAtService, false)
  const userResponsesManagerState = useServiceState(UserResponsesManagerService, false)
  const { emitError } = useNotification()

  const currentResponses = useMemo(
    () => responses || userResponsesManagerState.userResponses?.responses || ({} as IResponsesValue),
    [responses, userResponsesManagerState.userResponses]
  )

  const questions = useMemo(() => userResponsesManagerState.userResponses?.questions || {}, [
    userResponsesManagerState.userResponses
  ])

  useEffect(() => {
    ImGoodAtService.mapToState(currentResponses, questions)
  }, [currentResponses, questions])

  const silentSave = async () => {
    const isAllValid = ImGoodAtService.validateAllExpertises(questions)

    if (!isAllValid) {
      return
    }

    const expertises = ImGoodAtService.mapStateToExpertise(true)

    await UserResponsesManagerService.updateResponse({
      questionKey: 'expertise',
      response: expertises
    })
  }

  const onSubmitExpertise = async () => {
    const isAllValid = ImGoodAtService.validateAllExpertises(questions)

    if (!isAllValid) {
      return
    }

    const expertises = ImGoodAtService.mapStateToExpertise(true)

    const error = await UserResponsesManagerService.updateResponse({
      questionKey: 'expertise',
      response: expertises
    })

    if (error) {
      emitError(error)
      return
    }

    ImGoodAtService.setExpertiseEditingIndex(-1, questions)
  }

  const onSubmitBecomeExpertIn = async () => {
    const becomeExpertIn = ImGoodAtService.mapStateToBecomeExpertIn(true)

    const error = await UserResponsesManagerService.updateResponse({
      questionKey: 'becomeExpertIn',
      response: becomeExpertIn
    })

    if (error) {
      emitError(error)
      return
    }

    ImGoodAtService.setBecomeExpertInEditingIndex(-1)
  }

  return (
    <React.Fragment>
      {children({
        state: iAmGoodAtState,
        onAddBecomeExpertIn: () => ImGoodAtService.addNewBecomeExpertIn(),
        onAddExpertise: async () => {
          await silentSave()
          ImGoodAtService.addNewExpertise(questions)
        },
        onAddExpertiseTag: (index: number) => ImGoodAtService.addExpertiseTag(index),
        onBecomeExpertInItemChange: (index: number, value: string) =>
          ImGoodAtService.setBecomeExpertInItem(index, value),
        onEditBecomeExpertIn: (index: number) => ImGoodAtService.setBecomeExpertInEditingIndex(index),
        onEditExpertise: (index: number) => ImGoodAtService.setExpertiseEditingIndex(index, questions),
        onExpertiseTagLevelChange: (index, tagIndex, level) =>
          ImGoodAtService.setExpertiseTagLevel(index, tagIndex, level, questions),
        onExpertiseTagTitleChange: (index, tagIndex, title) =>
          ImGoodAtService.setExpertiseTagTitle(index, tagIndex, title, questions),
        onExpertiseTitleChange: (index, title) => ImGoodAtService.setExpertiseTitle(index, title, questions),
        onRemoveExpertise: async index => {
          ImGoodAtService.removeExpertise(index)
          await onSubmitExpertise()
        },
        onRemoveBecomeExpertIn: async index => {
          ImGoodAtService.removeBecomeExpertInItem(index)
          await onSubmitBecomeExpertIn()
        },
        onRemoveExpertiseTag: (index, tagIndex) => ImGoodAtService.removeExpertiseTag(index, tagIndex, questions),
        onSubmitBecomeExpertIn,
        onSubmitExpertise,
        silentSave,
        disabled,
        questions
      })}
    </React.Fragment>
  )
}

export default Provider
